import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertCircle, X, Megaphone, ChevronRight } from 'lucide-react';

const getApiBase = () => {
  const backend = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';
  if (!backend) return '/api';
  return backend.endsWith('/api') ? backend : `${backend}/api`;
};

const API = getApiBase();

const NoticeTicker = ({ user }) => {
  const [activeNotices, setActiveNotices] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const fetchActive = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/admin/announcements/active`, config);
      if (Array.isArray(res.data)) {
        setActiveNotices(res.data);
      }
    } catch (err) {
      console.error("Failed to load announcements:", err);
    }
  }, []);

  useEffect(() => {
    fetchActive();
    // Poll every 30 seconds for live updates
    const interval = setInterval(fetchActive, 30000);
    return () => clearInterval(interval);
  }, [fetchActive]);

  if (activeNotices.length === 0) return null;

  return (
    <>
      {/* Clickable Notice Ticker Bar */}
      <div 
        onClick={() => setShowModal(true)}
        className="group bg-gradient-to-r from-rose-600 via-rose-500 to-rose-600 text-white flex items-center px-4 sm:px-6 py-3 cursor-pointer hover:from-rose-700 hover:to-rose-700 transition-all shadow-md relative z-20"
        title="Click to view full notice"
      >
        <div className="flex items-center gap-2 font-black shrink-0 uppercase text-xs sm:text-sm bg-rose-950/70 backdrop-blur-md px-3.5 py-1.5 rounded-xl border border-rose-400/30 shadow-inner z-10">
          <AlertCircle className="w-4 h-4 text-rose-200 animate-pulse" />
          <span className="tracking-wide">Notice From HR👉</span>
        </div>
        
        <div className="flex-1 whitespace-nowrap overflow-hidden ml-4">
          <div className="inline-block animate-[marquee_25s_linear_infinite] group-hover:[animation-play-state:paused]">
            {activeNotices.map((notice, i) => (
              <span key={notice.id || i} className="mx-6 font-semibold text-sm sm:text-base tracking-wide" style={{ fontFamily: 'Outfit' }}>
                {notice.message} {i !== activeNotices.length - 1 && " • "}
              </span>
            ))}
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-1 text-xs font-bold bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg ml-3 shrink-0 transition-colors">
          <span>Read Full</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>

      {/* Readable Full Notice Modal Dialog */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-200"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-rose-600 to-rose-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg leading-tight" style={{ fontFamily: 'Outfit' }}>HR Announcements</h3>
                  <p className="text-xs text-rose-100 font-medium mt-0.5">{activeNotices.length} Active Notice{activeNotices.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4 custom-scrollbar">
              {activeNotices.map((notice, index) => (
                <div 
                  key={notice.id || index}
                  className="p-5 rounded-2xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200/80 dark:border-rose-900/50 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/50 px-2.5 py-1 rounded-md">
                      Notice #{index + 1}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Outfit' }}>
                    {notice.message}
                  </p>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200/80 dark:border-slate-800 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2.5 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 font-bold text-xs uppercase tracking-wider hover:opacity-90 transition-all shadow-sm cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </>
  );
};

export default NoticeTicker;